import { type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import {
  type GetPlacementsResponse,
  PlacementApplicationStatus,
} from "../types/fetchPlacements.schema";
import {
  type FetchPlacementsParams,
  useFetchPaginatedPlacements,
} from "./useFetchPaginatedPlacements";

/**
 * Default page size for pending interview placements.
 * Larger than default to enable client-side counting without additional requests.
 */
const DEFAULT_PAGE_SIZE = 200;

interface UseGetPendingInterviewPlacementsParams extends Omit<FetchPlacementsParams, "filter"> {
  additionalFilters?: FetchPlacementsParams["filter"];
}

/**
 * Fetch placements where the worker has a pending interview (interview:pending status).
 *
 * Pending interview placements are those where:
 * - Worker has an application with status "interview:pending"
 * - Interview has been booked but documents are not yet submitted (submit documents later flow)
 *
 * Note: Uses a larger page size (200) to enable efficient client-side counting.
 * For badge counts, use data?.pages[0]?.data.length ?? 0 from the first page.
 */
export function useGetPendingInterviewPlacements(
  params: UseGetPendingInterviewPlacementsParams,
  options: UseInfiniteQueryOptions<GetPlacementsResponse, AxiosError> = {}
) {
  const { workerId, placementCandidateId, additionalFilters, shouldLogEvent, sort, pageSize } =
    params;

  return useFetchPaginatedPlacements(
    {
      workerId,
      placementCandidateId,
      shouldLogEvent,
      sort,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
      filter: {
        ...additionalFilters,
        "applications.status": PlacementApplicationStatus.INTERVIEW_PENDING,
      },
    },
    options
  );
}
